import { Theme } from '@logto/schemas';
import { useEffect, useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';

const prefersDarkSchemeQuery = '(prefers-color-scheme: dark)';

const getDarkThemeWatchMedia = (): MediaQueryList | undefined => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return undefined;
  }

  return window.matchMedia(prefersDarkSchemeQuery);
};

export const getThemeBySystemConfiguration = (): Theme =>
  getDarkThemeWatchMedia()?.matches ? Theme.Dark : Theme.Light;

/**
 * BrightMusic patch: read the host app's explicit color scheme forwarded via the
 * `appearance_mode` authorize param (persisted by core into the `_logto` cookie).
 * Returns `undefined` when unset/unrecognized so callers fall back to the OS
 * `prefers-color-scheme`. Mirrors the pre-React bootstrap in `index.html`.
 */
const getExplicitAppearance = (): Theme | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  try {
    const match = document.cookie.match(/(?:^|;\s*)_logto=([^;]+)/);
    if (!match?.[1]) {
      return undefined;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(match[1]);
    } catch {
      parsed = JSON.parse(decodeURIComponent(match[1]));
    }

    const mode = (parsed as { appearanceMode?: unknown } | null)?.appearanceMode;
    if (mode === 'dark') {
      return Theme.Dark;
    }
    if (mode === 'light') {
      return Theme.Light;
    }
  } catch {
    // Ignore malformed cookies and fall back to the system preference.
  }

  return undefined;
};

export default function useTheme() {
  const { isPreview, experienceSettings, setTheme } = useContext(PageContext);

  useEffect(() => {
    if (!experienceSettings?.color.isDarkModeEnabled) {
      return;
    }

    // BrightMusic patch: an explicit app choice wins over the OS preference and, unlike
    // the system watcher below, must not be overridden by later OS scheme changes.
    const explicitAppearance = getExplicitAppearance();
    if (explicitAppearance) {
      setTheme(explicitAppearance);
      return;
    }

    const changeTheme = () => {
      setTheme(getThemeBySystemConfiguration());
    };

    changeTheme();

    const darkThemeWatchMedia = getDarkThemeWatchMedia();

    if (!darkThemeWatchMedia) {
      return;
    }

    darkThemeWatchMedia.addEventListener('change', changeTheme);

    return () => {
      darkThemeWatchMedia.removeEventListener('change', changeTheme);
    };
  }, [experienceSettings, isPreview, setTheme]);
}
